/**
 * Message shapes exchanged between the app and a sandboxed page over postMessage.
 * Both sides check `event.source`, never `event.origin`: the page has an opaque origin.
 * Page-side wrapper: src/pages-runtime/ts.js. Author docs: docs/custom-pages.md.
 */
import { z } from 'zod';

const requestId = z.string().min(1).max(64);

const filterGroupSchema = z.object({
	filters: z
		.array(
			z.object({
				field: z.string().min(1).max(200),
				operator: z.string().min(1).max(40),
				value: z.unknown().optional(),
			})
		)
		.max(50),
	// Models write 'AND' as often as 'and'; the page should not break over case.
	logicalOperator: z.preprocess((v) => (typeof v === 'string' ? v.toLowerCase() : v), z.enum(['and', 'or']).default('and')),
});

const slug = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
/** A path inside the version tree, as the Code panel shows it. */
const treePath = z
	.string()
	.max(200)
	.regex(/^[\w.-]+(\/[\w.-]+)*$/)
	.refine((p) => !p.split('/').includes('..'), 'no parent segments');

/** A YouTube video id as it appears in a watch or embed URL. */
export const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const versionIds = z.array(z.number().int().positive()).max(20);
const isoDate = z.string().max(40).refine((v) => !isNaN(Date.parse(v)), 'not a date');

export const pageRequestSchema = z.discriminatedUnion('type', [
	z.object({
		type: z.literal('ts.stat.count'),
		id: requestId,
		viewId: z.number().int().positive(),
		additionalFilters: filterGroupSchema.optional(),
	}),
	z.object({
		type: z.literal('ts.stat.timeseries'),
		id: requestId,
		viewId: z.number().int().positive(),
		field: z.string().min(1).max(200).optional(),
		interval: z.enum(['hour', 'day', 'week', 'month']).optional(),
		periodsBack: z.number().int().min(1).max(365).optional(),
		additionalFilters: filterGroupSchema.optional(),
	}),
	z.object({
		type: z.literal('ts.stat.metric'),
		id: requestId,
		key: z.string().regex(/^[a-z0-9_-]{1,40}$/),
		interval: z.enum(['day', 'week']).optional(),
		periodsBack: z.number().int().min(1).max(365).optional(),
		versionIds: versionIds.optional(),
		from: isoDate.optional(),
		to: isoDate.optional(),
	}),
	z.object({
		type: z.literal('ts.stat.metrics'),
		id: requestId,
	}),
	z.object({
		type: z.literal('ts.timeline.list'),
		id: requestId,
		from: isoDate.optional(),
		to: isoDate.optional(),
		kinds: z.array(z.enum(['version_live', 'experiment_changed', 'note'])).max(3).optional(),
		versionIds: versionIds.optional(),
	}),
	z.object({
		type: z.literal('ts.versions.list'),
		id: requestId,
	}),
	z.object({
		type: z.literal('ts.action.run'),
		id: requestId,
		actionId: z.string().min(1).max(200),
		userId: z.number().int().positive().optional(),
		input: z.record(z.unknown()).optional(),
	}),
	z.object({
		type: z.literal('ts.ui.selectUser'),
		id: requestId,
		userId: z.number().int().positive(),
	}),
	z.object({
		type: z.literal('ts.ui.toast'),
		id: requestId,
		title: z.string().min(1).max(200),
		description: z.string().max(1000).optional(),
		variant: z.enum(['default', 'destructive']).optional(),
	}),
	// Exactly one of view, page or file; the bridge checks that, since a discriminated union takes no refine.
	z.object({
		type: z.literal('ts.ui.navigate'),
		id: requestId,
		view: slug.optional(),
		page: slug.optional(),
		file: treePath.optional(),
	}),
	z.object({
		type: z.literal('ts.ui.playVideo'),
		id: requestId,
		youtubeId: z.string().regex(YOUTUBE_ID_RE),
		title: z.string().max(200).optional(),
	}),
]);

export type PageRequest = z.infer<typeof pageRequestSchema>;
export type PageRequestType = PageRequest['type'];

export const PAGE_REQUEST_TYPES: PageRequestType[] = pageRequestSchema.options.map((o) => o.shape.type.value);

export type PageNavigateTarget = { view: string } | { page: string } | { file: string };

export type PageBridgeErrorCode =
	| 'invalid_request'
	| 'unknown_type'
	| 'rate_limited'
	| 'view_not_found'
	| 'not_found'
	| 'http_error'
	| 'network_error';

export interface PageBridgeError {
	code: PageBridgeErrorCode;
	message: string;
	status?: number;
}

export type PageReply =
	| { type: 'ts.reply'; id: string; result: unknown }
	| { type: 'ts.reply'; id: string; error: PageBridgeError };

export type PageTheme = 'light' | 'dark';

export type PageEvent =
	| { type: 'ts.ready'; pageId: string; location: 'sidebar' | 'userTab' | 'settings'; userId?: number; theme: PageTheme }
	| { type: 'ts.user.update'; userId: number; data: Record<string, unknown> }
	| { type: 'ts.data.changed'; table: 'users' | 'user_history'; event?: string; userId?: number }
	| { type: 'ts.theme'; theme: PageTheme };

/** Fired on `window` by useAppState's realtime handlers; the bridge forwards it as `ts.data.changed`. */
export const DATA_CHANGED_DOM_EVENT = 'apprentice:data-changed';

export const PAGE_BRIDGE_RATE_LIMIT = { maxRequests: 30, windowMs: 10_000 } as const;
